import React, { useState, useEffect } from "react";
import {
  validateTaskInput,
  sanitizeTaskData,
  formatTime,
} from "../utils/helpers";
import TimeInput from "./TimeInput";
import SessionList from "./SessionList";
import { useSessionManager, sessionManager } from "../utils/sessionManager";

const InlineTaskForm = ({
  project,
  editingTask,
  onSave,
  onCancel,
  projects = [],
}) => {
  const [title, setTitle] = useState(editingTask?.title || "TASK");
  const [description, setDescription] = useState(
    editingTask?.description || ""
  );
  const [category, setCategory] = useState(editingTask?.category || "");
  const [selectedProjectId, setSelectedProjectId] = useState(
    editingTask?.projectId || project?.id || null
  );
  const [pendingSessions, setPendingSessions] = useState([]);

  // Use session manager for this task
  const { sessions, error, stats, timeBreakdown, createSession, refresh } =
    useSessionManager(editingTask?.id);

  // Calculate total time including pending sessions for new tasks
  const calculateTotalTime = () => {
    if (editingTask) {
      return stats.totalTime;
    } else {
      const pendingTime = pendingSessions.reduce(
        (total, session) => total + session.duration,
        0
      );
      return pendingTime;
    }
  };

  // Calculate time breakdown including pending sessions
  const calculateTimeBreakdown = () => {
    if (editingTask) {
      return timeBreakdown;
    } else {
      const manualTime = pendingSessions.reduce(
        (total, session) => total + session.duration,
        0
      );
      return {
        manual: manualTime,
        timer: 0,
        total: manualTime,
        manualCount: pendingSessions.length,
        timerCount: 0,
      };
    }
  };

  // Update form when editingTask changes
  useEffect(() => {
    if (editingTask) {
      setTitle(editingTask.title);
      setDescription(editingTask.description || "");
      setCategory(editingTask.category || "");
      setSelectedProjectId(editingTask.projectId || null);
    } else {
      setTitle("TASK");
      setDescription("");
      setCategory("");
      setSelectedProjectId(project?.id || null);
    }
  }, [editingTask, project]);

  const handleAddSession = async (sessionData) => {
    console.log("🟢 InlineTaskForm: handleAddSession called");
    console.log("  📊 Session Data:", sessionData);
    console.log("  📋 Editing Task:", editingTask ? editingTask.id : "None");
    console.log("  ⏰ Timestamp:", new Date().toISOString());

    try {
      if (editingTask) {
        // For existing tasks, create session immediately (auto-save)
        console.log("  🔄 Creating session for existing task:", editingTask.id);
        await createSession(sessionData);
        console.log("  ✅ Session created successfully for existing task");
      } else {
        // For new tasks, store as pending session
        console.log("  ⏳ Adding pending session for new task");
        setPendingSessions((prev) => {
          const newPending = [...prev, sessionData];
          console.log("  📝 Updated pending sessions:", newPending);
          return newPending;
        });
        console.log("  ✅ Pending session added successfully");
      }
    } catch (error) {
      console.error("  ❌ Error adding session:", error);
      alert("Error adding time session: " + error.message);
    }
  };

  const handleSessionChange = () => {
    refresh();
  };

  // Auto-save task changes for existing tasks
  const autoSaveTask = React.useCallback(
    async (updatedFields) => {
      if (!editingTask) return; // Only auto-save for existing tasks

      try {
        const taskData = {
          ...editingTask,
          ...updatedFields,
          updatedAt: new Date().toISOString(),
        };

        // Validate input
        const validation = validateTaskInput(taskData);
        if (!validation.isValid) {
          console.warn("Auto-save validation failed:", validation.errors);
          return;
        }

        // Sanitize data
        const sanitizedData = sanitizeTaskData(taskData);

        console.log("🔄 Auto-saving task changes:", sanitizedData);
        await onSave(sanitizedData);
        console.log("✅ Task auto-saved successfully");
      } catch (error) {
        console.error("❌ Auto-save failed:", error);
        // Don't show alert for auto-save failures to avoid interrupting user
      }
    },
    [editingTask, onSave]
  );

  // Debounced auto-save handlers
  const debouncedAutoSave = React.useMemo(() => {
    let timeoutId;
    return (updatedFields) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        autoSaveTask(updatedFields);
      }, 1000); // 1 second delay
    };
  }, [autoSaveTask]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const taskData = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      status: editingTask?.status || "todo",
      timeTracked: calculateTotalTime(), // Use calculated total from sessions
      projectId: selectedProjectId,
    };

    // If editing, include the task ID
    if (editingTask) {
      taskData.id = editingTask.id;
    }

    // Validate input
    const validation = validateTaskInput(taskData);
    if (!validation.isValid) {
      alert(
        `Please fix the following errors:\n${validation.errors.join("\n")}`
      );
      return;
    }

    // Sanitize data
    const sanitizedData = sanitizeTaskData(taskData);

    try {
      // Save the task
      console.log("InlineTaskForm: Saving task with data:", sanitizedData);
      const savedTask = await onSave(sanitizedData);
      console.log("InlineTaskForm: Saved task result:", savedTask);

      // If this was a new task with pending sessions, create them now
      if (!editingTask && pendingSessions.length > 0 && savedTask?.id) {
        console.log(
          "InlineTaskForm: Creating pending sessions:",
          pendingSessions,
          "for task:",
          savedTask.id
        );
        try {
          for (const sessionData of pendingSessions) {
            console.log("InlineTaskForm: Creating session:", sessionData);
            await sessionManager.createSession(savedTask.id, sessionData);
          }
          setPendingSessions([]); // Clear pending sessions
          console.log(
            "InlineTaskForm: All pending sessions created successfully"
          );
        } catch (sessionError) {
          console.error("Error creating pending sessions:", sessionError);
          alert(
            "Task saved but some time sessions could not be added: " +
              sessionError.message
          );
        }
      } else {
        console.log(
          "InlineTaskForm: No pending sessions to create. editingTask:",
          !!editingTask,
          "pendingSessions:",
          pendingSessions.length,
          "savedTask.id:",
          savedTask?.id
        );
      }

      // Reset form only if adding new task
      if (!editingTask) {
        setTitle("TASK");
        setDescription("");
        setCategory("");
        setSelectedProjectId(project?.id || null);
        setPendingSessions([]);
      }
    } catch (error) {
      console.error("Error saving task:", error);
      alert("Error saving task. Please try again.");
    }
  };

  const handleCancel = () => {
    // Reset form only if adding new task
    if (!editingTask) {
      setTitle("TASK");
      setDescription("");
      setCategory("");
      setSelectedProjectId(project?.id || null);
      setPendingSessions([]);
    }
    onCancel();
  };

  return (
    <div className={`task-item todo ${!editingTask ? "add-active" : ""}`}>
      <div className="task-header">
        <div className="task-info">
          <div className="task-details">
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                debouncedAutoSave({ title: e.target.value.trim() });
              }}
              className="task-title-input"
              placeholder="Task title"
            />
            <div className="task-meta">
              <div className="task-meta-row">
                <input
                  type="text"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    debouncedAutoSave({ category: e.target.value.trim() });
                  }}
                  placeholder="Category (optional)"
                  className="category-input"
                />
                <select
                  value={selectedProjectId || ""}
                  onChange={(e) => {
                    const newProjectId = e.target.value || null;
                    setSelectedProjectId(newProjectId);
                    debouncedAutoSave({ projectId: newProjectId });
                  }}
                  className="project-selector"
                >
                  <option value="">No Project</option>
                  {projects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      {proj.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Summary */}
              <div className="time-summary">
                <div className="time-stats">
                  <span className="time-label">
                    Total Time: {formatTime(calculateTotalTime())}
                  </span>
                  {calculateTimeBreakdown().manual > 0 && (
                    <span className="time-breakdown">
                      Manual: {formatTime(calculateTimeBreakdown().manual)} •
                      Timer: {formatTime(calculateTimeBreakdown().timer)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="task-expanded">
        <div className="task-description">
          <h5>Description:</h5>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              debouncedAutoSave({ description: e.target.value.trim() });
            }}
            placeholder="Description..."
            className="description-input"
            rows="4"
          />
        </div>

        {/* Time Management Section */}
        <div className="time-management-section">
          <h5>Time Management:</h5>

          {/* Add Time Input */}
          <TimeInput
            onAddSession={handleAddSession}
            placeholder="Add time (saves instantly): 2h 30m"
          />

          {/* Sessions List */}
          {(sessions.length > 0 || pendingSessions.length > 0) && (
            <div className="sessions-section">
              <SessionList
                sessions={editingTask ? sessions : pendingSessions}
                projects={projects}
                tasks={editingTask ? [editingTask] : []}
                onSessionChange={handleSessionChange}
                showStats={false}
                showGrouping={false}
              />
            </div>
          )}

          <div className="task-actions">
            {editingTask ? (
              // For existing tasks: Auto-save everything, just show close button
              <div className="auto-save-indicator">
                <span className="auto-save-text">✓ Auto-saved</span>
                <button className="close-btn" onClick={handleCancel}>
                  Close
                </button>
              </div>
            ) : (
              // For new tasks: Create button
              <div className="create-actions">
                <button className="cancel-btn" onClick={handleCancel}>
                  Cancel
                </button>
                <button className="create-btn" onClick={handleSubmit}>
                  Create Task
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="session-error">
              <span className="error-message">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InlineTaskForm;
